import { useState } from "react";
import { CellAiConfigView, UpdateCellAiConfigDocument } from "generated/graphql";
import { P } from "theme/typography";
import { LoadingButton } from "@mui/lab";
import PCheckbox from "components/PCheckbox";
import { useFormik } from "formik";
import { runGqlMutation } from "gqlClient";
import { useSnackbar } from "notistack";
import { fetchCellAiConfigs, selectLensEditorLensId } from "redux/slices/lensEditor";
import { useAppSelector } from "redux/hooks";
import { AutoAwesome } from "@mui/icons-material";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { nonEmpty } from "helpers/coll";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Box, TextField } from "@mui/material";
import SelectOptions from "components/SelectOptions/SelectOptions";

interface CellAIConfigFormProps {
  cellAiConfig?: CellAiConfigView;
  cellId: number;
}

const CellAIConfigForm = ({ cellAiConfig, cellId }: CellAIConfigFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isPending, setIsPending] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  const paramNumbers = useParamNumbers(["fieldId"]);
  const selectedFieldId = paramNumbers.fieldId;
  const lensId = useAppSelector(selectLensEditorLensId);
  const initialValues: CellAiConfigView = {
    additionalPrompt: cellAiConfig?.additionalPrompt,
    aiExclude: cellAiConfig?.aiExclude || false,
    alternateName: cellAiConfig?.alternateName,
    schemaValueName: cellAiConfig?.schemaValueName,
    cellId,
    valueLast: cellAiConfig?.valueLast ?? false,
    copoutSelectOptions: cellAiConfig?.copoutSelectOptions || [],
  };

  const form = useFormik({
    initialValues,
    onSubmit: (values) => {
      setIsPending(true);
      runGqlMutation(UpdateCellAiConfigDocument, values)
        .then(() => {
          enqueueSnackbar("Cell AI Config Saved", { variant: "success" });
          setIsPending(false);
          if (selectedFieldId)
            snackpatch(
              fetchCellAiConfigs({
                fieldId: selectedFieldId,
                lensId: lensId,
              }),
              {
                errorMessage: "Failed to fetch cell AI configs",
              }
            );
        })
        .catch(() => {
          enqueueSnackbar("Error Saving Cell AI Config", { variant: "error" });
          setIsPending(false);
        });
    },
  });

  return (
    <Box
      sx={{
        width: "100%",
        "& > * + *": {
          marginTop: 1,
        },
      }}>
      <Box
        sx={{
          marginBottom: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}>
        <AutoAwesome color="primary" />
        <P variant="lg">AI Config</P>
      </Box>
      <Box
        sx={{
          "& > * + *": {
            marginTop: 2,
          },
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}>
        <TextField
          label="Additional Prompt"
          value={form.values.additionalPrompt || ""}
          onChange={form.handleChange}
          id="additionalPrompt"
          multiline
          maxRows={8}
          rows={nonEmpty(form.values.additionalPrompt) ? 8 : 2}
        />
        <TextField
          label="Alternate Name"
          value={form.values.alternateName || ""}
          onChange={form.handleChange}
          id="alternateName"
        />
        <PCheckbox
          label="Exclude from AI Completion"
          id="aiExclude"
          checked={form.values.aiExclude}
          onChange={(e) => {
            form.setFieldValue("aiExclude", e.target.checked);
          }}
        />
        <TextField
          label="Schema Name"
          id="schemaValueName"
          value={form.values.schemaValueName || ""}
          onChange={form.handleChange}
        />
        <PCheckbox
          label="Value Last"
          id="valueLast"
          checked={form.values.valueLast}
          onChange={(e) => {
            form.setFieldValue("valueLast", e.target.checked);
          }}
        />
        <SelectOptions
          label="Copout Options"
          setOptions={(values) => form.setFieldValue("copoutSelectOptions", values)}
          options={form.values.copoutSelectOptions}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isPending}
            onClick={() => {
              form.handleSubmit();
            }}>
            {cellAiConfig ? "Update AI Config" : "Save AI Config"}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CellAIConfigForm;
